// streamManager.js
// A singleton manager that keeps streaming alive independent of React component lifecycle.

import Backend from "../../../common/utils/Backend";
import { dataURItoBlob } from "../../../common/utils/Utils";

const HEARTBEAT_INTERVAL = 3000; // 3 seconds without upload => consider not live

class StreamManager {
  constructor() {
    this._isStreaming = false;
    this._isLive = false;
    this._browserUUID = "";
    this._rendererProvider = null; // function returning current renderer or null
    this._loopRunning = false;
    this._lastUploadTime = 0;
    this._streamSubscribers = new Set(); // callbacks (isStreaming:boolean)
    this._liveSubscribers = new Set(); // callbacks (isLive:boolean)

    // Heartbeat to mark stream as not live if stalled
    setInterval(() => {
      if (!this._isStreaming) return;
      const now = Date.now();
      if (now - this._lastUploadTime > HEARTBEAT_INTERVAL) {
        this._setLive(false);
      }
    }, HEARTBEAT_INTERVAL);
  }

  subscribe(cb) {
    this._streamSubscribers.add(cb);
    return () => this._streamSubscribers.delete(cb);
  }

  subscribeLive(cb) {
    this._liveSubscribers.add(cb);
    return () => this._liveSubscribers.delete(cb);
  }

  _notifyStream() {
    for (const cb of Array.from(this._streamSubscribers)) {
      try {
        cb(this._isStreaming);
      } catch (e) {
        // ignore subscriber errors to avoid breaking the manager loop
      }
    }
  }

  _setLive(val) {
    if (this._isLive === val) return;
    this._isLive = val;
    for (const cb of Array.from(this._liveSubscribers)) {
      try {
        cb(this._isLive);
      } catch (e) {
        // ignore subscriber errors to avoid breaking the manager loop
      }
    }
  }

  isStreaming() {
    return this._isStreaming;
  }
  isLive() {
    return this._isLive;
  }

  setRendererProvider(provider) {
    this._rendererProvider = provider;
  }

  start({ browserUUID, rendererProvider } = {}) {
    if (browserUUID) this._browserUUID = browserUUID;
    if (rendererProvider) this._rendererProvider = rendererProvider;
    if (!this._browserUUID)
      throw new Error("StreamManager.start requires browserUUID");
    if (!this._rendererProvider)
      throw new Error("StreamManager.start requires rendererProvider");

    if (this._isStreaming) return; // already running
    this._isStreaming = true;
    this._notifyStream();
    if (!this._loopRunning) {
      this._loopRunning = true;
      this._uploadLoop();
    }
  }

  stop() {
    if (!this._isStreaming) return;
    this._isStreaming = false;
    this._setLive(false);
    this._notifyStream();
    // loop will exit naturally
  }

  async _uploadOnce() {
    if (!this._rendererProvider) return false;
    const renderer = this._rendererProvider();
    if (!renderer) return false;
    try {
      const img = renderer.canvas.toDataURL("image/jpeg", 0.9);
      await Backend.streamRenderer(dataURItoBlob(img), this._browserUUID);
      this._lastUploadTime = Date.now();
      this._setLive(true);
      return true;
    } catch (e) {
      // Network or backend hiccup; mark not live but keep trying
      this._setLive(false);
      return false;
    }
  }

  _uploadLoop = () => {
    if (!this._isStreaming) {
      this._loopRunning = false;
      return;
    }
    this._uploadOnce()
      .then((ok) => {
        const delay = ok ? 100 : 500;
        setTimeout(this._uploadLoop, delay);
      })
      .catch(() => {
        setTimeout(this._uploadLoop, 500);
      });
  };
}

// Singleton instance exposed across component mounts
const streamManager = new StreamManager();
export default streamManager;
